import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Avatar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
} from "@mui/material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import {
	actualizarAvatarUsuario,
	eliminarAvatarPersonaLuxand,
	subirAvatarUsuario,
} from "../../services/apis";
import { authFetch } from "../../services/apiClient";
import { hasAuthSession } from "../../services/authStorage";

const buildImageUrl = (personaId) =>
	`https://recon.jcinformatics.com/image.php?id_usuario=${personaId}&t=${Date.now()}`;

export function AvatarPersona({
	persona,
	size = 26,
	allowUpload = false,
	allowDeleteLuxand = false,
	parentDialogOpen = true,
	onAvatarUploaded,
	onAvatarDeleted,
}) {
	const [src, setSrc] = useState("");
	const [openPreview, setOpenPreview] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [deletingLuxand, setDeletingLuxand] = useState(false);
	const [imageUrl, setImageUrl] = useState(persona?.imagen_url || "");
	const fileInputRef = useRef(null);

	const nombrePersona = useMemo(() => {
		return (
			`${persona?.apellidos ?? ""} ${persona?.nombre ?? ""}`.trim() ||
			persona?.nombre_completo ||
			"Usuario"
		);
	}, [persona]);

	const iniciales = useMemo(() => {
		if (!nombrePersona) return "?";

		return nombrePersona
			.split(" ")
			.filter(Boolean)
			.slice(0, 2)
			.map((parte) => parte[0]?.toUpperCase())
			.join("");
	}, [nombrePersona]);

	const canUpload = allowUpload && !!persona?.id && hasAuthSession();
	const hasImage = !!imageUrl;
	const canShowDeleteLuxand = allowDeleteLuxand && hasImage && !!persona?.id && hasAuthSession();

	useEffect(() => {
		setImageUrl(persona?.imagen_url || "");
	}, [persona?.id, persona?.imagen_url]);

	useEffect(() => {
		if (parentDialogOpen === false) {
			setOpenPreview(false);
		}
	}, [parentDialogOpen]);

	useEffect(() => {
		let active = true;
		let objectUrl = null;

		const cargarImagen = async () => {
			if (!imageUrl || !hasAuthSession()) {
				setSrc("");
				return;
			}

			try {
				const res = await authFetch(imageUrl, {
					headers: {
						"Cache-Control": "no-cache",
						Pragma: "no-cache",
					},
				});

				if (!res.ok) throw new Error("No se pudo cargar la imagen");

				const blob = await res.blob();
				objectUrl = URL.createObjectURL(blob);

				if (active) setSrc(objectUrl);
			} catch (err) {
				console.error("Error cargando avatar:", err);
				if (active) setSrc("");
			}
		};

		cargarImagen();

		return () => {
			active = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [imageUrl]);

	const handleClosePreview = () => {
		setOpenPreview(false);
	};

	const handleAvatarClick = () => {
		if (uploading || deletingLuxand) return;

		if (src) {
			setOpenPreview(true);
			return;
		}

		if (canUpload) {
			fileInputRef.current?.click();
		}
	};

	const openFilePicker = (event) => {
		event?.stopPropagation?.();

		if (!canUpload || uploading || deletingLuxand) return;

		fileInputRef.current?.click();
	};

	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";

		if (!file) return;

		if (!file.type.startsWith("image/")) {
			window.alert("Selecciona una imagen valida.");
			return;
		}

		setUploading(true);

		try {
			const result = hasImage
				? await actualizarAvatarUsuario(persona.id, file)
				: await subirAvatarUsuario(persona.id, file);

			if (!result?.success) {
				window.alert(
					result?.message ||
						(hasImage
							? "No se pudo actualizar la imagen"
							: "No se pudo guardar la imagen")
				);
				return;
			}

			const updatedImageUrl = buildImageUrl(persona.id);
			setImageUrl(updatedImageUrl);

			if (typeof onAvatarUploaded === "function") {
				onAvatarUploaded(
					{
						...persona,
						imagen_url: updatedImageUrl,
					},
					result
				);
			}

			window.alert(
				hasImage ? "Avatar actualizado correctamente" : "Avatar guardado correctamente"
			);
		} catch (err) {
			console.error(
				hasImage ? "Error actualizando avatar:" : "Error subiendo avatar:",
				err
			);

			window.alert(
				hasImage ? "No se pudo actualizar la imagen" : "No se pudo subir la imagen"
			);
		} finally {
			setUploading(false);
		}
	};

	const handleDeleteLuxand = async () => {
		if (!canShowDeleteLuxand || !persona?.id) {
			return;
		}

		const confirmed = window.confirm(
			"Se eliminara el avatar actual de Luxand y dejara de estar activo para esta persona. Quieres continuar?"
		);
		if (!confirmed) {
			return;
		}

		setDeletingLuxand(true);

		try {
			const payload = {
				id_usuario: persona.id,
			};

			if (persona?.avatar_actual?.id) {
				payload.id_imagen = persona.avatar_actual.id;
			}

			const result = await eliminarAvatarPersonaLuxand(payload);

			if (!result?.success) {
				window.alert(result?.message || "No se pudo eliminar el avatar en Luxand.");
				return;
			}

			const updatedImageUrl = result?.data?.persona?.imagen_url || "";
			setImageUrl(updatedImageUrl);
			setOpenPreview(false);

			if (typeof onAvatarDeleted === "function") {
				onAvatarDeleted(result?.data?.persona || { ...persona, imagen_url: updatedImageUrl }, result);
			}

			window.alert(result?.message || "Avatar eliminado de Luxand correctamente.");
		} catch (error) {
			console.error("Error eliminando avatar en Luxand:", error);
			window.alert("No se pudo eliminar el avatar en Luxand.");
		} finally {
			setDeletingLuxand(false);
		}
	};

	return (
		<>
			<Box sx={{ position: "relative", display: "inline-flex" }}>
				<Avatar
					src={src || undefined}
					onClick={handleAvatarClick}
					title={
						src
							? canUpload
								? "Ver avatar"
								: "Ver avatar"
							: canUpload
								? "Subir avatar"
								: "Sin avatar"
					}
					sx={{
						width: size,
						height: size,
						cursor: src || canUpload ? "pointer" : "default",
						opacity: uploading ? 0.65 : 1,
					}}
				>
					{uploading ? <CircularProgress size={Math.max(size - 10, 16)} /> : !src ? iniciales : null}
				</Avatar>

				{canUpload && !uploading && (
					<IconButton
						size="small"
						onClick={openFilePicker}
						title={hasImage ? "Actualizar avatar" : "Subir avatar"}
						sx={{
							position: "absolute",
							right: -6,
							bottom: -6,
							width: Math.max(18, Math.round(size * 0.52)),
							height: Math.max(18, Math.round(size * 0.52)),
							bgcolor: "primary.main",
							color: "common.white",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							boxShadow: 1,
							"&:hover": {
								bgcolor: "primary.dark",
							},
						}}
					>
						<AddAPhotoIcon sx={{ fontSize: Math.max(10, Math.round(size * 0.24)) }} />
					</IconButton>
				)}

				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					onChange={handleFileChange}
					style={{ display: "none" }}
				/>
			</Box>

			{parentDialogOpen && openPreview && (
				<Dialog open onClose={handleClosePreview} maxWidth="sm" fullWidth>
					<DialogTitle>{nombrePersona}</DialogTitle>
					<DialogContent sx={{ display: "flex", justifyContent: "center", p: 2 }}>
						<Box
							component="img"
							src={src}
							alt={nombrePersona}
							sx={{
								maxWidth: "100%",
								maxHeight: "70vh",
								objectFit: "contain",
								borderRadius: 1,
							}}
						/>
					</DialogContent>
					<DialogActions>
						{canShowDeleteLuxand && (
							<Button color="error" onClick={handleDeleteLuxand} disabled={uploading || deletingLuxand}>
								{deletingLuxand ? "Eliminando..." : "Eliminar Avatar"}
							</Button>
						)}
						{canUpload && (
							<Button onClick={openFilePicker} disabled={uploading || deletingLuxand}>
								Actualizar avatar
							</Button>
						)}
						<Button onClick={handleClosePreview} disabled={deletingLuxand}>
							Cerrar
						</Button>
					</DialogActions>
				</Dialog>
			)}
		</>
	);
}
